import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Linking,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

interface LocationMapProps {
  location: string;
  height?: number;
  showControls?: boolean;
}

const LocationMap: React.FC<LocationMapProps> = ({
  location,
  height = 200,
  showControls = true,
}) => {
  const { colors } = useTheme();
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const geocodeLocation = useCallback(async (locationString: string) => {
    if (!locationString) return;

    setLoading(true);
    setError(null);

    try {
      // Use Nominatim (OpenStreetMap) geocoding API - free and no API key required
      const encodedLocation = encodeURIComponent(locationString);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedLocation}&limit=1`
      );

      if (!response.ok) {
        throw new Error('Geocoding failed');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        setCoordinates({
          lat: parseFloat(lat),
          lng: parseFloat(lon),
        });
      } else {
        setError('Location not found');
      }
    } catch {
      setError('Could not find location on map');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (location) {
      void geocodeLocation(location);
    }
  }, [location, geocodeLocation]);

  const openInMaps = () => {
    if (!coordinates) return;

    const { lat, lng } = coordinates;
    const url = Platform.select({
      ios: `maps://app?daddr=${lat},${lng}`,
      android: `geo:${lat},${lng}?q=${lat},${lng}(${encodeURIComponent(location)})`,
      default: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
    });

    if (url) {
      Linking.openURL(url).catch(() => {});
    }
  };

  const openInGoogleMaps = () => {
    if (!coordinates) return;

    const { lat, lng } = coordinates;
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    Linking.openURL(url).catch(() => {});
  };

  if (!location) {
    return null;
  }

  // For web, use an iframe with OpenStreetMap
  if (Platform.OS === 'web') {
    if (loading) {
      return (
        <View
          style={[
            styles.container,
            { height, backgroundColor: colors.background },
          ]}
        >
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: colors.secondary }]}>
            Loading map...
          </Text>
        </View>
      );
    }

    if (error || !coordinates) {
      return (
        <View
          style={[
            styles.container,
            styles.errorContainer,
            { height, backgroundColor: colors.background },
          ]}
        >
          <Ionicons
            name="location-outline"
            size={32}
            color={colors.secondary}
          />
          <Text style={[styles.errorText, { color: colors.secondary }]}>
            {error || 'Could not display map'}
          </Text>
          {showControls && (
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.primary }]}
              onPress={openInGoogleMaps}
            >
              <Text style={styles.buttonText}>Open in Google Maps</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }

    // For web, use a clickable image that links to the map
    return (
      <View style={[styles.container, { height }]}>
        <TouchableOpacity
          style={styles.mapWrapper}
          onPress={openInGoogleMaps}
          activeOpacity={0.9}
        >
          <View
            style={[
              styles.mapPlaceholder,
              { height, backgroundColor: colors.background },
            ]}
          >
            <Ionicons name="map-outline" size={48} color={colors.primary} />
            <Text style={[styles.mapPlaceholderText, { color: colors.text }]}>
              {location}
            </Text>
            <Text
              style={[
                styles.mapPlaceholderSubtext,
                { color: colors.secondary },
              ]}
            >
              Tap to view on map
            </Text>
            <View
              style={[
                styles.mapOverlay,
                { backgroundColor: colors.card + '80' },
              ]}
            >
              <Ionicons name="open-outline" size={20} color={colors.primary} />
              <Text style={[styles.overlayText, { color: colors.primary }]}>
                Open in Maps
              </Text>
            </View>
          </View>
        </TouchableOpacity>
        {showControls && (
          <View style={styles.controls}>
            <TouchableOpacity
              style={[styles.controlButton, { backgroundColor: colors.card }]}
              onPress={openInGoogleMaps}
            >
              <Ionicons name="open-outline" size={16} color={colors.primary} />
              <Text style={[styles.controlText, { color: colors.primary }]}>
                Open in Maps
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  }

  // For native platforms, show a placeholder with option to open in maps app
  return (
    <View
      style={[
        styles.container,
        styles.nativeContainer,
        { height, backgroundColor: colors.background },
      ]}
    >
      {loading ? (
        <>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: colors.secondary }]}>
            Loading map...
          </Text>
        </>
      ) : error || !coordinates ? (
        <>
          <Ionicons
            name="location-outline"
            size={48}
            color={colors.secondary}
          />
          <Text style={[styles.errorText, { color: colors.secondary }]}>
            {error || 'Could not find location'}
          </Text>
          {showControls && (
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.primary }]}
              onPress={openInMaps}
            >
              <Ionicons name="map-outline" size={20} color="#fff" />
              <Text style={styles.buttonText}>Open in Maps</Text>
            </TouchableOpacity>
          )}
        </>
      ) : (
        <>
          <Ionicons name="map-outline" size={48} color={colors.primary} />
          <Text style={[styles.locationText, { color: colors.text }]}>
            {location}
          </Text>
          <Text style={[styles.coordinatesText, { color: colors.secondary }]}>
            {coordinates.lat.toFixed(4)}, {coordinates.lng.toFixed(4)}
          </Text>
          {showControls && (
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.primary }]}
              onPress={openInMaps}
            >
              <Ionicons name="navigate-outline" size={20} color="#fff" />
              <Text style={styles.buttonText}>Open in Maps App</Text>
            </TouchableOpacity>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    overflow: 'hidden',
    marginVertical: 8,
  },
  mapWrapper: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  mapPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  mapPlaceholderText: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  mapPlaceholderSubtext: {
    marginTop: 4,
    fontSize: 12,
    textAlign: 'center',
  },
  mapOverlay: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  overlayText: {
    fontSize: 12,
    fontWeight: '600',
  },
  nativeContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  errorContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    textAlign: 'center',
  },
  locationText: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  coordinatesText: {
    marginTop: 4,
    fontSize: 12,
    textAlign: 'center',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 16,
    gap: 8,
  },
  buttonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  controls: {
    position: 'absolute',
    bottom: 12,
    right: 12,
  },
  controlButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  controlText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

export default LocationMap;
